/**
 * Warden glue: the hooks the game fills in (players, world, sounds, particles, Darkness) and the vibration bus.
 * Dependency-free (types only) so the one-line `wardenHooks.vibrate(...)` calls in the player-action sites
 * (audio/hooks.ts, entities/arrow.ts) never pull the entity code in.
 *
 * Vanilla game events the warden (and the sculk shrieker) feel, with the anger a vibration adds:
 *   step / land / jump / swim (not while sneaking), block_break, block_place, container, eat, shoot  -> +35
 *   projectile_land (an arrow sticks in a block)                                                     -> +10
 * Vibrations pass through blocks (only wool stops them in vanilla; not modelled here).
 */

import type { World } from '../../world/chunk';
import type { PlayerTarget } from '../host';
import type { Mob } from '../mob';
import type { MobType } from '../../game/types';

export type VibrationKind =
  | 'step' | 'land' | 'jump' | 'swim'
  | 'block_break' | 'block_place' | 'container' | 'eat' | 'shoot' | 'projectile_land';

/** Anger a vibration of this kind adds (vanilla default 35; a projectile landing is only 10). */
export const vibrationAnger = (k: VibrationKind): number => (k === 'projectile_land' ? 10 : 35);

/**
 * The vibration a player's movement makes this tick, or null. Sneaking (and flying) players make none.
 * `stepped` is the footstep tracker's verdict for this tick ('step' on the ground, 'swim' in water).
 */
export function movementVibration(m: {
  stepped: 'step' | 'swim' | null; sneaking: boolean; flying: boolean;
  wasOnGround: boolean; onGround: boolean; inWater: boolean; vy: number;
}): VibrationKind | null {
  if (m.sneaking || m.flying) return null;
  if (m.stepped) return m.stepped;
  if (m.wasOnGround && !m.onGround && m.vy > 0.1) return 'jump';
  if (m.onGround && !m.wasOnGround && !m.inWater) return 'land';
  return null;
}

/** Something that reacts to vibrations: a warden, or the shrieker system. */
export type VibrationSink = (x: number, y: number, z: number, kind: VibrationKind, source: string | null) => void;

export interface WardenFx {
  /** A sonic boom beam from (x0, y0, z0) to (x1, y1, z1). */
  boom(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): void;
}

export const wardenHooks = {
  /** Every player the entity system simulates against (local + remote). */
  players: (): readonly PlayerTarget[] => [],
  world: (): World | null => null,
  mobs: (): readonly Mob[] => [],
  spawn: ((_t: MobType, _x: number, _y: number, _z: number): Mob | null => null) as (t: MobType, x: number, y: number, z: number) => Mob | null,
  rnd: (): number => Math.random(),
  /** Game tick counter (EntityManager's). */
  tick: (): number => 0,
  /** World difficulty ('peaceful' keeps shriekers from summoning). */
  difficulty: (): string => 'normal',
  sound: ((_id: string, _x: number, _y: number, _z: number): void => undefined) as (id: string, x: number, y: number, z: number) => void,
  fx: { boom: () => undefined } as WardenFx,
  /** Give a player the Darkness effect for `ticks` (the local player's effect set; remote peers get none). */
  darkness: ((_p: PlayerTarget, _ticks: number): void => undefined) as (p: PlayerTarget, ticks: number) => void,
  /** Upward impulse on a player (sonic boom's vertical knockback). */
  toss: ((_p: PlayerTarget, _vy: number): void => undefined) as (p: PlayerTarget, vy: number) => void,

  /** Wardens and the shrieker system subscribe here. */
  sinks: new Set<VibrationSink>(),

  /**
   * A vibration at (x, y, z). `source` is the id of the player responsible ('' = the local player), or null
   * for an unowned event (wardens ignore those). Call from the place the action happens; cheap when nobody listens.
   */
  vibrate(x: number, y: number, z: number, kind: VibrationKind, source: string | null = ''): void {
    for (const s of wardenHooks.sinks) s(x, y, z, kind, source);
  },
};
